import {
  ClinicProduct,
  ClinicProductQuery,
  ListResult,
} from '../types';
import { USE_MOCK_PRODUCTS } from '../constants/buyer-portal';
import { productMock } from '../mocks/productMock';
import { clinicHttp } from './http';
import { ENDPOINTS } from './endpoints';
import { mapProductBuyerDto, ProductBuyerDtoBackend } from './productMapper';

export type ProductFacets = {
  brands: { key: string; count: number }[];
  priceMin: number;
  priceMax: number;
  items: ClinicProduct[];
};

export type ProductService = {
  list: (query?: ClinicProductQuery) => Promise<ListResult<ClinicProduct>>;
  detail: (productId: string) => Promise<ClinicProduct>;
  facets: () => Promise<ProductFacets>;
};

type BackendListResult = {
  items: ProductBuyerDtoBackend[] | null;
  skip: number;
  take: number;
  total: number;
};

const computeFacets = (items: ClinicProduct[]): ProductFacets => {
  const brandCounts = new Map<string, number>();
  let min = Infinity;
  let max = 0;
  for (const p of items) {
    if (p.brand) brandCounts.set(p.brand, (brandCounts.get(p.brand) ?? 0) + 1);
    if (p.salePrice < min) min = p.salePrice;
    if (p.salePrice > max) max = p.salePrice;
  }
  return {
    brands: Array.from(brandCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count),
    priceMin: Math.floor(min === Infinity ? 0 : min),
    priceMax: Math.ceil(max || 0),
    items,
  };
};

const realProductService: ProductService = {
  list: async (query = {}) => {
    const res = await clinicHttp.get<BackendListResult>(ENDPOINTS.BUYER.PRODUCTS, {
      params: query,
    });
    return {
      items: (res.data.items ?? []).map(mapProductBuyerDto),
      skip: res.data.skip,
      take: res.data.take,
      total: res.data.total,
    };
  },
  detail: async (productId) => {
    const res = await clinicHttp.get<ProductBuyerDtoBackend>(
      ENDPOINTS.BUYER.PRODUCT_DETAIL(productId),
    );
    return mapProductBuyerDto(res.data);
  },
  facets: async () => {
    // Backend has no dedicated facets endpoint. Pull a wide page and aggregate.
    const res = await clinicHttp.get<BackendListResult>(ENDPOINTS.BUYER.PRODUCTS, {
      params: { take: 200 },
    });
    return computeFacets((res.data.items ?? []).map(mapProductBuyerDto));
  },
};

export const productService: ProductService = USE_MOCK_PRODUCTS
  ? productMock
  : realProductService;
